// The region palette-swap seam behind two playtest findings (bee-body-palette): "bee colours
// don't match the types properly — the WHOLE bee body should be coloured rather than just a
// band around the neck" (finding 4) and "all enemy bees should be shown with red bands
// instead of black bands so they are visibly different to the player's bees" (finding 6).
//
// The shipped directional sheets are 8-colour pixel art whose colours partition cleanly into
// three regions — transparent, wing, band/outline, body — so a bee's whole body can be
// recoloured on the CPU once per variant, at asset-load, instead of drawing a tinted overlay
// per bee per frame. Pixi-free and unit-tested without a GPU, like `season-ground.ts`,
// `draw-list.ts`, `facing.ts`, `blob-colony.ts` and `ground-scatter.ts`; the texture
// realisation + caching in `renderer.ts` is device-exercised only (repo convention D-086).
//
// This closes D-090 by a different mechanism than that deferral anticipated: rather than
// shipping five per-preset directional sheets, ONE generic body is recoloured per type — which
// also covers the player-defined types beyond the five presets (up to 12 per colony, DESIGN §3)
// that a fixed sheet set never could.

import { COLONY_PALETTE_SIZE } from '../ui/match/colony-visuals';

// --- the source sheets' palette (verified by decoding all three bee PNGs) ---

/** The bee body's source shading ramp, DARK → LIGHT. Five entries: the four the `worker`
 * fly/walk sheets use plus the one extra dark shade `bee_queen_walk_dir.png` adds, so the
 * one ramp covers every bee sheet and no pixel is left un-recoloured. */
export const BEE_SOURCE_BODY_RAMP: readonly number[] = Object.freeze([
  0xa96f14, 0xd98e1a, 0xe8a020, 0xf0b429, 0xf0ce74,
]);

/** The band/outline source ramp, DARK → LIGHT: the near-black stripes and the sprite's own
 * outline. Recoloured only for an enemy (finding 6); an own/neutral bee keeps it. */
export const BEE_SOURCE_BAND_RAMP: readonly number[] = Object.freeze([0x2b2114, 0x4a3820]);

/** The pale translucent wing colour. Never recoloured — a wing is a wing whoever owns it,
 * and leaving it fixed keeps the recoloured bodies reading against each other. */
export const BEE_SOURCE_WING = 0xe0eef9;

// --- the target palettes (pinned to `ui/tokens/colors.css` by `bee-palette.test.ts`) ---
//
// The canvas needs concrete RGB, not `var(--token)` strings, so these are lifted here as
// numbers and a test pins them equal to the CSS the React surfaces read. That keeps the
// dock/type-sheet swatch and the bee on the field the same colour — which is what finding 4
// ("colours don't match the types") is actually about.

/** The five shipped presets' colours, indexed by the wire `type_id` — which IS the colony
 * type-order index, and the canonical `Preset` roster order is fixed at
 * `0=harvester, 1=nurse, 2=builder, 3=scout, 4=warrior` (`waggle-fixtures/src/preset.rs`,
 * `waggle-server/src/colony_assembly.rs` `PRESET_NAMES`). */
export const BEE_PRESET_COLORS: readonly number[] = Object.freeze([
  0xe8a020, // --bee-harvester
  0xa6713f, // --bee-nurse
  0xd97a9e, // --bee-builder
  0x3e7ca6, // --bee-scout
  0xc4472e, // --bee-warrior
]);

/** The extension ramp for player-defined types past the five presets (UI.md §2, DESIGN §3):
 * `ramp[(typeId - 5) mod 7]`, the same rule the colony screen's swatches use. */
export const BEE_EXT_COLORS: readonly number[] = Object.freeze([
  0x5c8f63, 0x3f9e8f, 0x6c7fbf, 0xa65fa0, 0xb85c3d, 0x8fa63e, 0xc45c86,
]);

/** The queen's colour (`--bee-queen`). She is a sixth, typeless entity with a native
 * controller, not a B++ type (DESIGN §3), so she gets her own token rather than a slot in
 * the type ramp. */
export const BEE_QUEEN_COLOR = 0x7a5aa6;

/** The per-colony identity ramp (`--colony-N`), indexed `colonyId mod COLONY_PALETTE_SIZE` —
 * the same palette the leaderboard dot, wide-tier swarm blob, and hive marker already share
 * (`ui/match/colony-visuals.ts`). */
export const COLONY_COLORS: readonly number[] = Object.freeze([
  0xe8a020, 0xc4472e, 0x3e7ca6, 0x7a5aa6, 0x5f8a34, 0xd97a9e, 0xb0b84b, 0x4ba79a,
]);

/** The three flat season ground fills (`season-ground.ts`'s `GROUND_FILL_COLOR`), mirrored
 * here as the backdrop an enemy body must stay legible against. Duplicated rather than
 * imported to keep the module graph one-directional (`renderer` → both), and pinned equal to
 * the source table by test. */
export const SEASON_GROUND_COLORS: readonly number[] = Object.freeze([
  0x7ca74b, 0xc0913f, 0xc3cbce,
]);

/** The minimum perceptual separation ({@link colorDistance}) a bee body must keep from every
 * season ground. Chosen from the measured distances of the authored palette, which fall into
 * two clear groups: `--colony-4` (82 from summer), `--colony-6` (84 from autumn) and
 * `--colony-0` (87 from autumn) collide; every other colony sits at 148 or above. 120 splits
 * that gap, so the guard fires on exactly the three that camouflage and touches nothing else. */
export const MIN_FIELD_COLOR_DISTANCE = 120;

let liveColonyColors: readonly number[] = COLONY_COLORS;

/** Installs a cached presentation palette snapshot. Returns a generation-safe restore closure so
 * the renderer's per-bee hot path stays pure and performs no DOM/style resolution. */
export function setColonyColorOverrides(colors: readonly [number, number]): () => void {
  const previous = liveColonyColors;
  liveColonyColors = Object.freeze([colors[0], colors[1], ...COLONY_COLORS.slice(2)]);
  const installed = liveColonyColors;
  return () => {
    if (liveColonyColors === installed) liveColonyColors = previous;
  };
}

function liveColonyColor(colonyId: number): number {
  const index = ((colonyId % COLONY_PALETTE_SIZE) + COLONY_PALETTE_SIZE) % COLONY_PALETTE_SIZE;
  return liveColonyColors[index]!;
}

/**
 * "Redmean" weighted RGB distance — a cheap, well-behaved approximation of perceptual colour
 * difference. Plain luminance distance is the wrong metric here: `--colony-1` (#C4472E, red)
 * has almost the same luminance as the summer grass yet is obviously distinguishable, while
 * `--colony-4` (#5F8A34) shares the grass's *hue* and vanishes into it. Redmean captures both.
 */
export function colorDistance(a: number, b: number): number {
  const r1 = (a >> 16) & 0xff;
  const g1 = (a >> 8) & 0xff;
  const b1 = a & 0xff;
  const r2 = (b >> 16) & 0xff;
  const g2 = (b >> 8) & 0xff;
  const b2 = b & 0xff;
  const rm = (r1 + r2) / 2;
  const dr = r1 - r2;
  const dg = g1 - g2;
  const db = b1 - b2;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}

/** A colour's hue in DEGREES (0..360, matching `toHsl`) — exported so the contrast guard
 * can be pinned as hue-preserving. */
export function hueOf(color: number): number {
  return toHsl(color)[0];
}

/**
 * Guarantees a body colour stays legible against every season ground (playtest follow-up).
 *
 * The `--colony-N` palette was authored for leaderboard dots and wide-tier blobs, whose
 * backdrop is UI chrome. On the field three of its entries collide with a season fill —
 * `--colony-4` (#5F8A34) and `--colony-6` (#B0B84B) against the summer/autumn grounds, and
 * `--colony-0` (#E8A020) against the autumn gold — so an enemy bee from those colonies
 * camouflaged, defeating finding 6's entire purpose. Verified by rendering the real recoloured
 * sprites over the real fills.
 *
 * The guard **darkens** (never lightens) toward black until the body clears
 * {@link MIN_FIELD_COLOR_DISTANCE} against all three grounds. Darkening preserves hue and
 * saturation, so the colony still reads as the same colour as its wide-tier blob and
 * leaderboard dot — the cross-surface identity the palette exists for — while separating it
 * from the field. A colour that already clears every ground is returned unchanged, so the
 * five well-behaved colonies keep their exact authored value and the guard is idempotent.
 */
export function fieldSafeBodyColor(color: number): number {
  const clears = (c: number): boolean =>
    SEASON_GROUND_COLORS.every((g) => colorDistance(c, g) >= MIN_FIELD_COLOR_DISTANCE);
  if (clears(color)) {
    return color;
  }
  const [h, sat, startL] = toHsl(color);
  // Walk lightness down in small steps; every ground fill is mid-to-light, so darkening
  // always converges (black clears all three by construction).
  for (let l = startL - 0.02; l > 0; l -= 0.02) {
    const candidate = toRgb(h, sat, l);
    if (clears(candidate)) {
      return candidate;
    }
  }
  return 0x000000;
}

/** The band colour every enemy bee's outline takes (`--danger`) — finding 6's "red bands
 * instead of black bands so that they are visibly different to the player's bees". */
export const ENEMY_BAND_COLOR = 0xc4472e;

/**
 * The red band for an enemy whose body has been darkened by {@link fieldSafeBodyColor}.
 *
 * The band is an OUTLINE: it only reads as an edge while it stays darker than the body it
 * encloses. `ENEMY_BAND_COLOR` is fixed, so a body pushed dark enough to clear the field can
 * end up *darker than its own outline*, inverting the sprite's read. This keeps the band's
 * red hue and saturation and clamps its lightness below the body's, so the edge survives the
 * guard. A body that was never darkened leaves the band at its authored value.
 */
export function enemyBandFor(body: number): number {
  const [bh, bs, bl] = toHsl(ENEMY_BAND_COLOR);
  const bodyL = toHsl(body)[2];
  // Minimal intervention: the band's own remap factors already darken it well below its
  // target, so clamping the TARGET to the body's lightness is enough to keep the rendered
  // band ramp under the rendered body ramp. Anything stricter would re-tune the band for
  // the colonies the guard never touched.
  const ceiling = bodyL;
  return bl <= ceiling ? ENEMY_BAND_COLOR : toRgb(bh, bs, ceiling);
}

// --- the remap ---

/** Body-ramp remap factors: the output ramp spans `targetL × 0.66` .. `targetL × 1.34`, so a
 * body reads AS its target colour with the source's shading spread around it. */
const BODY_RAMP_FACTORS = { lo: 0.66, hi: 0.68 } as const;

/** Band-ramp remap factors, deliberately darker and narrower than the body's
 * (`targetL × 0.35` .. `targetL × 0.9`). The band is an OUTLINE and roughly 40% of a bee's
 * opaque pixels: run through the body factors it lightens to a salmon that swamps the colony
 * colour. These keep it an unmistakably red but still dark edge (validated against a
 * rendered sheet of all eight colony colours). */
const BAND_RAMP_FACTORS = { lo: 0.35, hi: 0.55 } as const;

/** How a bee is coloured relative to the viewer: `own` (the player's own colony), `enemy`
 * (any other colony while the player has one), or `neutral` (a spectator / pre-handshake
 * viewer has no own colony, so nobody is an enemy). */
export type ColonyRole = 'own' | 'enemy' | 'neutral';

/** A bee's role for `ownColonyId` — `null` (spectator, or before the `Welcome` lands) makes
 * every colony neutral rather than making every colony an enemy. */
export function colonyRoleFor(colonyId: number, ownColonyId: number | null): ColonyRole {
  if (ownColonyId === null) return 'neutral';
  return colonyId === ownColonyId ? 'own' : 'enemy';
}

/** The recolour a bee's sprite sheet is realised with. `band === null` keeps the source band
 * ramp untouched (own + neutral bees stay dark-banded); `key` identifies the realised texture
 * variant. */
export interface BeeTint {
  /** The variant cache key. Derived from the RESOLVED colours, not from `(typeId, role)`, so
   * two bees that happen to resolve to the same pair share one texture and the key is
   * self-describing. */
  readonly key: string;
  /** The body ramp's target colour (0xRRGGBB). */
  readonly body: number;
  /** The band ramp's target colour, or `null` to keep the source's dark band. */
  readonly band: number | null;
}

/** The colour ramp an own-colony `type_id` maps to: the five presets by their canonical
 * roster index, then the extension ramp, wrapped so any index is well-defined. */
function ownTypeColor(typeId: number): number {
  if (typeId < BEE_PRESET_COLORS.length) {
    return BEE_PRESET_COLORS[typeId];
  }
  const n = BEE_EXT_COLORS.length;
  return BEE_EXT_COLORS[(((typeId - BEE_PRESET_COLORS.length) % n) + n) % n];
}

/**
 * The tint one close-tier bee's body draws with (bee-body-palette).
 *
 * - **Own colony:** the bee TYPE's colour over the whole body, band left dark — the type is
 *   the thing the player is programming, so it is the thing the body carries.
 * - **Enemy:** the bee's own COLONY colour with a red band. Never the type colour: DESIGN §5
 *   makes type names colony-private (an enemy is matched by buffed characteristic, never by
 *   name), so rendering an enemy's type colour would leak private information.
 * - **Neutral (spectator):** the colony colour with the dark source band — a spectator has no
 *   enemies, and the colony colour is already the Match screen's identity for that colony.
 *
 * The typeless queen (`typeId === null`, DESIGN §3) takes `--bee-queen` in her own colony and
 * the ordinary enemy/neutral rule elsewhere — she has no type to colour by.
 */
export function beeTintFor(
  colonyId: number,
  typeId: number | null,
  ownColonyId: number | null,
): BeeTint {
  const role = colonyRoleFor(colonyId, ownColonyId);
  const body =
    role === 'own'
      ? typeId === null
        ? BEE_QUEEN_COLOR
        : ownTypeColor(typeId)
      : // An enemy/neutral body takes the colony colour through the field-contrast guard: the
        // authored palette was made for UI-chrome backdrops, and three of its entries
        // camouflage against a season ground (see `fieldSafeBodyColor`).
        fieldSafeBodyColor(liveColonyColor(colonyId));
  const band = role === 'enemy' ? enemyBandFor(body) : null;
  return { key: `${hex(body)}/${band === null ? 'src' : hex(band)}`, body, band };
}

function hex(rgb: number): string {
  return rgb.toString(16).padStart(6, '0');
}

/** HSL of a packed 0xRRGGBB, as `[hueDegrees, saturation, lightness]`. */
function toHsl(rgb: number): [number, number, number] {
  const r = ((rgb >> 16) & 0xff) / 255;
  const g = ((rgb >> 8) & 0xff) / 255;
  const b = (rgb & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return [h < 0 ? h + 360 : h, s, l];
}

/** Packs an HSL triple back to 0xRRGGBB (the same conversion `danceTagColor` uses). */
function toRgb(h: number, s: number, l: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const q = (v: number): number => Math.round((v + m) * 255) & 0xff;
  return (q(r) << 16) | (q(g) << 8) | q(b);
}

/**
 * Remaps one source ramp onto `target`, preserving each entry's RELATIVE position within the
 * ramp's own lightness range — the pixel art's shading survives instead of flattening to a
 * single flat colour. Each entry takes the target's hue and saturation, and a lightness of
 * `targetL × lo + t × targetL × hi` where `t` is the entry's normalised position (0 at the
 * ramp's darkest, 1 at its lightest).
 */
function remapRamp(
  ramp: readonly number[],
  target: number,
  factors: { lo: number; hi: number },
): number[] {
  const lightnesses = ramp.map((c) => toHsl(c)[2]);
  const lo = Math.min(...lightnesses);
  const hi = Math.max(...lightnesses);
  const [th, ts, tl] = toHsl(target);
  return ramp.map((_, i) => {
    const t = hi > lo ? (lightnesses[i] - lo) / (hi - lo) : 0.5;
    const l = Math.min(1, Math.max(0, tl * factors.lo + t * tl * factors.hi));
    return toRgb(th, ts, l);
  });
}

/** The source-RGB → replacement-RGB lookup for one tint. Keyed on RGB alone (alpha is always
 * carried through from the source pixel), which is unambiguous because every colour in the
 * bee sheets is distinct in RGB. */
function buildLut(tint: BeeTint): Map<number, number> {
  const lut = new Map<number, number>();
  remapRamp(BEE_SOURCE_BODY_RAMP, tint.body, BODY_RAMP_FACTORS).forEach((c, i) => {
    lut.set(BEE_SOURCE_BODY_RAMP[i], c);
  });
  if (tint.band !== null) {
    remapRamp(BEE_SOURCE_BAND_RAMP, tint.band, BAND_RAMP_FACTORS).forEach((c, i) => {
      lut.set(BEE_SOURCE_BAND_RAMP[i], c);
    });
  }
  return lut;
}

/**
 * Returns a NEW RGBA buffer with `src`'s body (and, for an enemy tint, band) region pixels
 * replaced per {@link beeTintFor} — the whole-body recolour finding 4 asks for. Pure: `src`
 * is never mutated, and the same `(src, tint)` always yields the same bytes.
 *
 * Every pixel outside the two ramps passes through byte-for-byte (transparent pixels, the
 * translucent wing, and — for an own/neutral tint — the dark band), and every recoloured
 * pixel keeps its SOURCE alpha, so the sheet's anti-aliasing and translucency are unchanged.
 * Callers realise this once per `(sheet, tint.key)` variant at first use and cache the
 * resulting texture; it is never a per-bee or per-frame cost.
 */
export function recolorBeePixels(src: Uint8ClampedArray, tint: BeeTint): Uint8ClampedArray {
  const lut = buildLut(tint);
  const out = new Uint8ClampedArray(src);
  for (let i = 0; i < out.length; i += 4) {
    if (out[i + 3] === 0) {
      continue; // fully transparent: nothing to recolour, and its RGB is meaningless
    }
    const replacement = lut.get((out[i] << 16) | (out[i + 1] << 8) | out[i + 2]);
    if (replacement === undefined) {
      continue; // outside both ramps (the wing, or a kept band): pass through
    }
    out[i] = (replacement >> 16) & 0xff;
    out[i + 1] = (replacement >> 8) & 0xff;
    out[i + 2] = replacement & 0xff;
  }
  return out;
}
